import {first, isEqual} from 'lodash';
import {ReactNode} from 'react';
import {connect} from 'react-redux';

import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {UnwrappedActionCreator} from '../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {CellsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {computeCellsFilters} from '../../../../../srcCommon/inboxes/cells/cellsFiltersSelectors';
import {RInboxesFilter} from '../../../../models/url/models/inboxesUrlModel';
import {AppState} from '../../../../store/appState';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {updateSelectedFilterAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getShouldUseOpenFilterForTeams} from '../../../../store/data/preferences/preferencesSelectors';
import {getStarredTagIds} from '../../../../store/data/tags/tagsSelectors';
import {
  CellsPanelPageSelector,
  makeGetCellsPanelCurrentPage,
  makeGetCellsPanelPage,
} from '../../../../store/panels/cells/cellsPanelSelectors';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  isInboxesConversationsParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  clearInboxesParametersSelection,
  inboxParametersHasAssigneeLensCriteria,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersPath} from '../../../../store/panels/inboxes/parameters/inboxesParametersPathHelpers';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {CellsHeader} from './cellsHeader';
import {Filter} from './cellsHeaderFilters';

/*
 * Props.
 */

interface CellsHeaderContainerProps {
  panelId: CellsPanelId;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: AppState, props: CellsHeaderContainerProps) {
  return props.panelId;
}

function makeGetCellsPanelPageParameters<P>(getPage: CellsPanelPageSelector<P>) {
  return createSelector([getPage], (page) => page.parameters);
}

/*
 * Filters selectors.
 */

export function makeGetFilterProps<P>(
  getPage: CellsPanelPageSelector<P>,
  getCurrentPage: CellsPanelPageSelector<P>,
) {
  return createSelector(
    [getPage, getCurrentPage, getStarredTagIds, getShouldUseOpenFilterForTeams, getLenses],
    (page, currentPage, starredTagIds, shouldUseOpenFilterForTeams, lenses) => {
      // If there's only one page, use its filters.
      const filters = findFilters({
        starredTagIds,
        parameters: page.parameters,
        shouldUseOpenFilterForTeams,
        shouldUseOpenFilterForLenses: inboxParametersHasAssigneeLensCriteria(page.parameters, lenses),
      });

      if (currentPage === page) {
        return filters;
      }

      // Otherwise, compare their filters.
      // We only use the page filters if they match.
      const currentFilters = findFilters({
        starredTagIds,
        parameters: currentPage.parameters,
        shouldUseOpenFilterForTeams,
        shouldUseOpenFilterForLenses: inboxParametersHasAssigneeLensCriteria(currentPage.parameters, lenses),
      });
      if (currentFilters && filters && isEqual(currentFilters.filters, filters.filters)) {
        return filters;
      }

      // Otherwise, use the current filters.
      return currentFilters;
    },
  );
}

interface Filters {
  filters: ReadonlyArray<Filter>;
  selectedFilter: Filter;
  selectedFilters: ReadonlyArray<Filter>;
  localSearchFilterName?: ReactNode;
}

interface FindFiltersOptions {
  starredTagIds: ReadonlySet<number>;
  parameters: InboxesParameters;
  shouldUseOpenFilterForTeams: boolean;
  shouldUseOpenFilterForLenses: boolean;
}

export function findFilters(options: FindFiltersOptions): Filters | undefined {
  const {starredTagIds, parameters, shouldUseOpenFilterForTeams, shouldUseOpenFilterForLenses} = options;
  const filters = computeCellsFilters(
    starredTagIds,
    shouldUseOpenFilterForTeams,
    shouldUseOpenFilterForLenses,
    parameters,
  );

  // Some folders don't have any filters.
  const defaultFilter = first(filters);
  if (!defaultFilter) {
    return undefined;
  }

  // Make sure at least one filter is selected.
  const selectedFilters =
    isInboxesConversationsParameters(parameters) && parameters.filters.length > 0
      ? parameters.filters
      : [defaultFilter];

  return {
    filters,
    selectedFilter: selectedFilters[0],
    selectedFilters,
  };
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CellsHeaderContainerProps>()(() => {
  const getCellsPanelPage = makeGetCellsPanelPage(getCellsPanelId);
  const getCellsPanelCurrentPage = makeGetCellsPanelCurrentPage(getCellsPanelId);
  const getCellsPanelPageParameters = makeGetCellsPanelPageParameters(getCellsPanelPage);

  return {
    parameters: getCellsPanelPageParameters,
    filters: makeGetFilterProps(getCellsPanelPage, getCellsPanelCurrentPage),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<CellsHeaderContainerProps>()(() => ({
  navigateToLinkAction,
  updateSelectedFilterAction,
}));

export const CellsHeaderContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {parameters, filters: stateFilters} = stateProps;

    return {
      ...ownProps,
      ...stateProps,
      filters: makeCellsFilterProps(parameters, stateFilters, dispatchProps.navigateToLinkAction),
    };
  },
)(CellsHeader);

export function makeCellsFilterProps(
  parameters: InboxesParameters,
  stateFilters: Filters | undefined,
  navigateToLink: UnwrappedActionCreator<typeof navigateToLinkAction>,
) {
  return (
    stateFilters && {
      ...stateFilters,
      onMultipleFiltersSelect: (newSelectedFilters: ReadonlyArray<Filter>) => {
        // Filters only apply to conversation lists.
        if (!isInboxesConversationsParameters(parameters)) {
          return;
        }

        const newFilters = newSelectedFilters.filter(RInboxesFilter.is);

        // Multiple filters only apply to inbox filters.
        if (newFilters.length === 0) {
          return;
        }

        // Track chosen filters
        trackInterfaceEvent(InterfaceEventsEnum.CELLS_FILTERS_SELECTED, {
          tab_names: newFilters,
          tab_count: newFilters.length,
        });

        const newParameters: InboxesConversationsParameters = {
          ...clearInboxesParametersSelection(parameters),
          filters: newFilters,
        };
        navigateToLink(buildParametersPath(newParameters));
      },
    }
  );
}
